// App shell: the session gate, hash routing over the four tabs, service-worker
// registration, and the offline check-in queue flush hooks. Every mount runs
// inside an error boundary — a view that throws must never leave a blank page.

import "./styles.css";
import { getSession, logout, refresh } from "./auth";
import { applyTextSize, setToken } from "./config";
import { clear, el } from "./dom";
import { initQueueFlush } from "./queue";
import { t, type StringKey } from "./strings";
import { mountIdag } from "./views/idag";
import { mountInstallningar } from "./views/installningar";
import { mountOnboarding } from "./views/onboarding";
import { mountMat } from "./views/mat";
import { mountPrata } from "./views/prata";
import { mountResa } from "./views/resa";

type TabId = "idag" | "mat" | "prata" | "resa" | "installningar";

interface Tab {
  id: TabId;
  label: StringKey;
  icon: string; // inline SVG path data, 24x24 grid
  mount: (root: HTMLElement) => () => void;
}

const TABS: Tab[] = [
  {
    id: "idag",
    label: "tabs.today",
    icon: "M12 3l8 7v10a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1V10l8-7z",
    mount: mountIdag,
  },
  {
    id: "mat",
    label: "tabs.mat",
    icon: "M7 3v5a2 2 0 0 0 4 0V3M9 3v18M17 3v18M17 3c2.2 1.6 2.2 6.4 0 8",
    mount: mountMat,
  },
  {
    id: "prata",
    label: "tabs.talk",
    icon: "M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-5 4V5z",
    mount: mountPrata,
  },
  {
    id: "resa",
    label: "shell.tabs.journey",
    icon: "M4 20V4m0 16h16M7 16l4-5 3 3 5-7",
    mount: mountResa,
  },
  {
    id: "installningar",
    label: "tabs.settings",
    icon: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm8.4 4a8.4 8.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a8.5 8.5 0 0 0-2-1.2L15.5 3h-4l-.4 2.6a8.5 8.5 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a8.4 8.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a8.5 8.5 0 0 0 2 1.2l.4 2.6h4l.4-2.6a8.5 8.5 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.07-.4.1-.8.1-1.2z",
    mount: mountInstallningar,
  },
];

const ONBOARDED_KEY = "phoenix.onboarded";
/** Renew a token quietly once it has less than three days left. */
const REFRESH_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

const viewRoot = document.getElementById("view") as HTMLElement;
const tabBar = document.getElementById("tabs") as HTMLElement;

let dispose: (() => void) | null = null;
let activeTab: TabId | null = null;

function icon(path: string): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", path);
  svg.append(p);
  return svg;
}

function renderTabs(): void {
  clear(tabBar);
  for (const tab of TABS) {
    const btn = el("button", { class: "tab", type: "button" });
    btn.append(icon(tab.icon), el("span", { text: t(tab.label) }));
    if (tab.id === activeTab) btn.setAttribute("aria-current", "page");
    btn.addEventListener("click", () => {
      location.hash = `#/${tab.id}`;
    });
    tabBar.append(btn);
  }
}

function disposeCurrent(): void {
  const fn = dispose;
  dispose = null;
  if (!fn) return;
  try {
    fn();
  } catch {
    /* a broken view must not block leaving it */
  }
}

/** Plain-Swedish fallback so a crashing view never becomes an empty screen. */
function renderErrorCard(): void {
  const card = el("div", { class: "card" });
  card.append(
    el("h2", { text: t("shell.error.title") }),
    el("p", { class: "muted", text: t("shell.error.body") })
  );
  const retry = el("button", { class: "btn", type: "button", text: t("shell.error.retry") });
  retry.addEventListener("click", () => route());
  card.append(retry);
  viewRoot.append(card);
}

function activate(tab: Tab): void {
  disposeCurrent();
  activeTab = tab.id;
  clear(viewRoot);
  tabBar.hidden = false;
  renderTabs(); // before the mount, so the tabs survive a view that throws
  try {
    dispose = tab.mount(viewRoot);
  } catch {
    clear(viewRoot);
    renderErrorCard();
  }
  viewRoot.scrollTop = 0;
}

// Landing, login and the first-run walkthrough share one surface; it reads the
// hash itself to know which of the two it is showing.
function showOnboarding(): void {
  disposeCurrent();
  activeTab = null;
  clear(viewRoot);
  clear(tabBar);
  tabBar.hidden = true; // no tabs before the app is his
  try {
    const view = mountOnboarding(viewRoot);
    dispose = () => view.unmount();
  } catch {
    clear(viewRoot);
    renderErrorCard();
  }
  viewRoot.scrollTop = 0;
}

/** A stored session whose expiry has passed is no session at all. */
function hasSession(): boolean {
  const session = getSession();
  if (!session) return false;
  if (session.expiresAt > 0 && session.expiresAt <= Date.now()) {
    logout();
    return false;
  }
  return true;
}

function route(): void {
  const hash = location.hash.replace(/^#\//, "");
  if (!hasSession()) {
    if (hash !== "valkommen") {
      location.hash = "#/valkommen";
      return; // the hashchange brings us straight back here
    }
    showOnboarding();
    return;
  }
  if (localStorage.getItem(ONBOARDED_KEY) !== "1") {
    if (hash !== "start") {
      location.hash = "#/start";
      return;
    }
    showOnboarding();
    return;
  }
  const tab = TABS.find((candidate) => candidate.id === hash);
  if (!tab) {
    location.hash = "#/idag";
    return;
  }
  activate(tab);
}

window.addEventListener("hashchange", route);

// Register the service worker up front so push subscriptions made in
// Inställningar find a controlled page, and notification clicks focus us.
if ("serviceWorker" in navigator) {
  void navigator.serviceWorker.register("/sw.js").catch(() => {
    /* dev without https or unsupported — features degrade gracefully */
  });
}

applyTextSize();

// Keep the Authorization header in step with the stored session, and renew a
// token before it runs out so a daily user is never logged out mid-morning.
const session = getSession();
if (session) {
  setToken(session.token);
  if (session.expiresAt - Date.now() < REFRESH_WINDOW_MS) void refresh();
}

initQueueFlush();
route();
